import React, {useState, useEffect, useRef, useMemo} from "react";
import {useSwipeable} from "react-swipeable";

// Models
import {AuthUser} from "../../../Models/DTOs/AuthUser";
import {InfoCardActionDTO, InfoCardDTO} from "../../../Models/DTOs/InfoCardDTO";

// Components
import ButtonsPrevNext from "../../Buttons/ButtonsPrevNext";
import {renderInfoCardAction} from "./InfoCardActions";

// Helpers
import {debounce} from "../../../Helpers/Performance/Performance";
import {mergeRefs} from "../../../Helpers/Refs";

// Hooks
import useFetchFeaturedSlider from "../../../Hooks/Content/useFetchFeaturedSlider";
import useFetchInfoCards from "../../../Hooks/Content/useFetchInfoCards";
import VideoLoadingOverlay from "../../LoadingOverlay";

type SliderFeaturedContentProps = {
    sliderId: number;
    classNameInner?: string;
    userDetails: AuthUser | Record<string, never> | null;
};

const
    BREAKPOINT_SMALL_TABLET = 584,
    BREAKPOINT_TABLET = 760,
    BREAKPOINT_WIDE = 1272,
    BREAKPOINT_SUPER_WIDE = 1840,
    BREAKPOINT_MAX = 1900,
    BREAKPOINTS = [
        {name: "mobile", value: 0},
        {name: "small-tablet", value: BREAKPOINT_SMALL_TABLET},
        {name: "tablet", value: BREAKPOINT_TABLET},
        {name: "wide", value: BREAKPOINT_WIDE},
        {name: "super-wide", value: BREAKPOINT_SUPER_WIDE},
        {name: "max", value: BREAKPOINT_MAX},
    ];

const SliderFeaturedContent: React.FC<SliderFeaturedContentProps> = (
    {
        sliderId,
        classNameInner,
        userDetails,
    }
) => {
    const
        {slider, loading: sliderLoading} = useFetchFeaturedSlider(sliderId, 0),
        infoCardIds = useMemo(() => slider?.items.map((x) => x.infoCardId), [slider?.items]),
        {cards, loading: cardsLoading} = useFetchInfoCards(infoCardIds ?? [], 0),

        [slides, setSlides] = useState<InfoCardDTO[]>([]),
        [currentSlide, setCurrentSlide] = useState(0),

        sliderTimer = 10000,
        radius = 9,
        circumference = 2 * Math.PI * radius,

        slideTimeoutRef = useRef<NodeJS.Timeout | null>(null),
        rafRef = useRef<number | null>(null),
        startTsRef = useRef<number>(0),
        circleRef = useRef<SVGCircleElement | null>(null),
        slidesLenRef = useRef<number>(slides?.length || 1),

        [breakpointClasses, setBreakpointClasses] = useState("is-breakpoint-mobile"),
        containerRef = useRef<HTMLDivElement>(null),

        stopTimers = () => {
            if (slideTimeoutRef.current) {
                clearTimeout(slideTimeoutRef.current);
                slideTimeoutRef.current = null;
            }
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        },

        startRadialRAF = () => {
            // reset ring to full "remaining" at start
            if (circleRef.current) {
                circleRef.current.style.strokeDasharray = `${circumference}`;
                circleRef.current.style.strokeDashoffset = `${0}`; // 0 = full remaining (we'll animate to circumference)
            }

            startTsRef.current = performance.now();

            const tick = (now: number) => {
                const elapsed = now - startTsRef.current;
                const t = Math.min(1, elapsed / sliderTimer);

                // We want "remaining" feel:
                // 0 (full remaining) -> circumference (empty) over time
                const dashOffset = t * circumference;

                if (circleRef.current) {
                    circleRef.current.style.strokeDashoffset = `${dashOffset}`;
                }

                if (t < 1) {
                    rafRef.current = requestAnimationFrame(tick);
                } else {
                    rafRef.current = null;
                }
            };

            rafRef.current = requestAnimationFrame(tick);
        },

        scheduleAutoAdvance = () => {
            slideTimeoutRef.current = setTimeout(() => {
                setCurrentSlide((prev) => (prev + 1) % (slidesLenRef.current || 1));
            }, sliderTimer);
        },

        resetSlideCycle = () => {
            stopTimers();
            startRadialRAF();
            scheduleAutoAdvance();
        },

        handleSlideChange = (slideIndex: number) => {
            setCurrentSlide(slideIndex);
        },

        handleSwipeLeft = () => {
            setCurrentSlide((prev) => (prev + 1) % (slides?.length || 1));
        },

        handleSwipeRight = () => {
            setCurrentSlide((prev) => (prev - 1 + (slides?.length || 1)) % (slides?.length || 1));
        },

        handleOnClickNext = () => {
            setCurrentSlide((prev) => (prev + 1) % (slides?.length || 1));
        },

        handleOnClickPrev = () => {
            setCurrentSlide((prev) => (prev - 1 + (slides?.length || 1)) % (slides?.length || 1));
        },

        swipeHandlers = useSwipeable({
            onSwipedLeft: handleSwipeLeft,
            onSwipedRight: handleSwipeRight,
        }),

        renderButton = (action: InfoCardActionDTO, key: React.Key) => renderInfoCardAction(action, key);

    useEffect(() => {
        resetSlideCycle();
        return () => stopTimers();
    }, [slides?.length]);

    useEffect(() => {
        resetSlideCycle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSlide]);

    useEffect(() => {
        setSlides(cards)
    }, [cards]);

    useEffect(() => {
        slidesLenRef.current = Math.max(1, slides.length);

        // optional: clamp currentSlide if slides shrink
        setCurrentSlide((prev) => Math.min(prev, slidesLenRef.current - 1));
    }, [slides.length]);

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return;

        const updateBreakpoint = () => {
            const width = node.clientWidth;
            if (width) {
                const classes = BREAKPOINTS
                    .filter((b) => width >= b.value)
                    .map((b) => `is-breakpoint-${b.name}`)
                    .join(" ");
                setBreakpointClasses(classes);
            }
        };
        updateBreakpoint();

        const debouncedUpdate = debounce(updateBreakpoint, 200);
        if (typeof ResizeObserver !== "undefined") {
            const resizeObserver = new ResizeObserver(() => debouncedUpdate());
            resizeObserver.observe(node);
            return () => resizeObserver.disconnect();
        } else {
            window.addEventListener("resize", debouncedUpdate);
            return () => window.removeEventListener("resize", debouncedUpdate);
        }
    }, []);

    return (
        <div
            {...swipeHandlers}
            // @ts-ignore
            ref={mergeRefs(containerRef, swipeHandlers.ref)}
            className={`layout--slider-featured-content ${breakpointClasses} ${sliderLoading || cardsLoading ? 'is-loading' : 'is-not-loading'}`}
        >
            <div
                className={`slider-featured-content__inner ${classNameInner ?? ""}`}
                style={{
                    transform: `translateX(-${currentSlide * (100 / (slides?.length || 1))}%)`,
                    display: "flex",
                    width: `${(slides?.length || 1) * 100}%`,
                }}
            >
                {sliderLoading || cardsLoading ? (
                    <VideoLoadingOverlay hideLoader={!sliderLoading && !cardsLoading} size="full-cover"/>
                ) : (
                    slides &&
                    slides.map((slide, index) => (
                        <div
                            key={index}
                            className={`slider-featured-content__slide u-relative_hidden ${
                                currentSlide === index ? "is-active" : "is-not-active"
                            }`}
                            style={{background: "#000000", width: `${100 / (slides?.length || 1)}%`, flexShrink: 0}}
                        >
                            <div
                                className="u-full_cover_absolute"
                                style={{
                                    background: `url("${slide.bgImageUrl}") center/cover no-repeat`,
                                    opacity: "0.5"
                                }}
                            />
                            <div className="slider-featured-content__content">
                                <h1>
                                    <span>{slide.heading}</span>
                                </h1>
                                <h2>
                                    <span>{slide.subHeading}</span>
                                </h2>
                                <div className="slider-featured-content__button-wrapper u-relative_hidden">
                                    {(slide.actions ?? []).map((action, j) => renderButton(action, `${slide.infoCardId}-${j}`))}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {slides && slides.length > 1 && (
                <>
                    <div className="slider-featured-content__dots">
                        {slides.map((_, index) => (
                            <div
                                key={index}
                                onClick={() => handleSlideChange(index)}
                                className={`slider-featured-content__dot-control ${
                                    currentSlide === index ? "is-active" : "is-not-active"
                                }`}
                            />
                        ))}
                    </div>

                    <ButtonsPrevNext
                        onPrev={handleOnClickPrev}
                        onNext={handleOnClickNext}
                        currentIndex={currentSlide}
                        parentClass="slider-featured-content__controls"
                    />

                    <svg className="slider-featured-content__radial-timer" width="22" height="22" viewBox="0 0 22 22">
                        <circle
                            ref={circleRef}
                            stroke="white"
                            fill="transparent"
                            strokeWidth="4"
                            strokeDasharray={circumference}
                            strokeDashoffset={0}
                            r={9}
                            cx="11"
                            cy="11"
                        />
                    </svg>
                </>
            )}

            <div className="slider-featured-content__shadow u-full_cover_absolute"/>
        </div>
    );
};

export default SliderFeaturedContent;
